import React from "react";
import { IndianRupee, FileText, Globe2, CheckCircle2, AlertCircle, Coins, ArrowRight } from "lucide-react";

const SectionLabel = ({ children }) => (
  <div className="text-center text-red-400 text-[11.5px] tracking-[0.22em] uppercase font-medium">
    {children}
  </div>
);

const OfferCard = ({ Icon, badge, title, desc, foot, highlight }) => (
  <div
    className={`relative rounded-2xl p-6 md:p-7 ${
      highlight ? "cc-card-amber" : "cc-card"
    } transition-transform duration-300 hover:-translate-y-1`}
  >
    {badge && (
      <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-red-500 text-white text-[10.5px] tracking-[0.16em] uppercase font-semibold px-3 py-1">
        ✨ {badge}
      </span>
    )}
    <div className="h-12 w-12 rounded-xl bg-amber-500/15 grid place-items-center mb-5">
      <Icon className="h-6 w-6 text-amber-400" />
    </div>
    <h3 className="text-white text-[20px] font-semibold">{title}</h3>
    <p className="mt-3 text-slate-300 text-[14.5px] leading-relaxed">{desc}</p>
    <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-3 py-1 text-emerald-300 text-[12.5px]">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {foot}
    </div>
  </div>
);

const Condition = ({ n, title, desc }) => (
  <div className="rounded-2xl cc-card p-5 md:p-6">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-red-500/15 text-red-300 grid place-items-center font-semibold text-[14px] border border-red-500/30">
        {n}
      </div>
      <div className="text-[10.5px] tracking-[0.18em] uppercase text-slate-400">Condition {n}</div>
    </div>
    <h4 className="mt-4 text-white text-[16.5px] font-semibold leading-snug">{title}</h4>
    <p className="mt-2.5 text-slate-300 text-[14px] leading-relaxed">{desc}</p>
  </div>
);

const OfferSection = () => {
  return (
    <section id="offer" className="py-14 md:py-24 bg-[#0b1220]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionLabel>The Offer</SectionLabel>
        <h2 className="mt-3 text-center text-white text-[34px] md:text-[44px] font-semibold leading-[1.1] tracking-tight">
          The cashback. The conditions.
          <br className="hidden md:block" />
          <span className="text-amber-400"> All clear.</span>
        </h2>
        <p className="mt-4 text-center text-slate-300 max-w-2xl mx-auto">
          Enrol for an Australia, UK or New Zealand university through Competitive Careers Pvt Ltd — and we'll refund your exam fee.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-5 md:gap-6">
          <OfferCard
            Icon={IndianRupee}
            badge="Most Popular"
            title="EXAM FEE Cashback"
            desc="Already paid your IELTS, PTE, TOEFL or LanguageCert exam fee? We refund the amount of the latest attempt once your enrolment is confirmed. Terms apply."
            foot="IELTS · PTE · TOEFL · LanguageCert"
            highlight
          />
          <OfferCard
            Icon={FileText}
            title="Free Application & Visa Support"
            desc="University shortlisting, SOP writing, document checklist, applications and student visa filing — handled by our team at zero processing cost."
            foot="End-to-end. Zero processing fee."
          />
          <OfferCard
            Icon={Globe2}
            title="Anywhere in India"
            desc="Online application, document review and counselling from any city in India. No need to travel to Ahmedabad to apply."
            foot="Online · Flexible · Personal"
          />
        </div>

        <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 md:p-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-slate-200 text-[14.5px] leading-relaxed">
            <span className="font-semibold text-white">Note:</span> This promotion is for students who intend to study in{" "}
            <span className="text-amber-300 font-medium">Australia</span>,{" "}
            <span className="text-amber-300 font-medium">UK</span> or{" "}
            <span className="text-amber-300 font-medium">New Zealand</span>. Canada has a separate dedicated promotion — ask our team.
          </p>
        </div>

        {/* Conditions */}
        <div className="mt-12 rounded-3xl border border-white/8 bg-[#0e1626]/70 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-500/15 grid place-items-center">
              <Coins className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <div className="text-[10.5px] tracking-[0.18em] uppercase text-red-400">Cashback Conditions</div>
              <h3 className="text-white text-[22px] md:text-[26px] font-semibold tracking-tight">
                Three simple conditions to claim your exam fee cashback
              </h3>
            </div>
          </div>

          <div className="mt-7 grid md:grid-cols-3 gap-5">
            <Condition
              n="01"
              title="Offer letter through CCPL OR change of agent to CCPL"
              desc="Receive your university offer letter via Competitive Careers Pvt Ltd, or switch your existing application from another agent to CCPL."
            />
            <Condition
              n="02"
              title="Whole process through CCPL"
              desc="Complete your application, SOP, documentation and visa filing with our team end to end."
            />
            <Condition
              n="03"
              title="Enrol at AU / UK / NZ"
              desc="Final enrolment at an Australia, UK or New Zealand university through CCPL."
            />
          </div>

          <p className="mt-7 text-slate-300 text-[14px] leading-relaxed">
            Once all three conditions are met, your IELTS / PTE / TOEFL / LanguageCert exam fee is refunded after enrolment confirmation. For verification, share the{" "}
            <span className="text-white font-medium">official exam fee receipt</span> — or, if unavailable, your{" "}
            <span className="text-white font-medium">original exam result / score report</span>. If you have appeared for the exam multiple times, only the{" "}
            <span className="text-white font-medium">latest attempt's</span> fee is eligible for cashback.{" "}
            <a
              href="https://www.competitivecareers.in/"
              target="_blank"
              rel="noreferrer"
              className="text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 underline-offset-4 hover:underline"
            >
              Learn more about CCPL <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default OfferSection;
